import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

// Mock order data
const mockOrders = [
  {
    id: 1,
    poNumber: 'PO-2024-001',
    orderDate: '2024-01-15',
    customerName: 'ABC Distributors',
    customerEmail: 'purchasing@abcdist.com',
    status: 'completed',
    totalAmount: 2450.75,
    items: [
      { name: 'Sur La Table Cookware Set', quantity: 2, price: 179.99 },
      { name: 'Babish Cast Iron Pan', quantity: 5, price: 34.97 }
    ],
    shippingAddress: {
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      country: 'USA'
    },
    estimatedDelivery: '2024-01-22',
    actualDelivery: '2024-01-21',
    carrier: 'UPS',
    trackingNumber: '1Z999AA10123456784'
  },
  {
    id: 2,
    poNumber: 'PO-2024-002',
    orderDate: '2024-01-18',
    customerName: 'XYZ Retail Chain',
    customerEmail: 'orders@xyzretail.com',
    status: 'shipped',
    totalAmount: 1875.50,
    items: [
      { name: 'Gibson Soho Lounge Dinnerware Set', quantity: 3, price: 29.99 },
      { name: 'Babish Cast Iron Pan', quantity: 10, price: 34.97 }
    ],
    shippingAddress: {
      street: '456 Oak Avenue',
      city: 'Chicago',
      state: 'IL',
      zipCode: '60601',
      country: 'USA'
    },
    estimatedDelivery: '2024-01-25',
    actualDelivery: null,
    carrier: 'FedEx',
    trackingNumber: '789012345678'
  },
  {
    id: 3,
    poNumber: 'PO-2024-003',
    orderDate: '2024-01-20',
    customerName: 'Regional Home Stores',
    customerEmail: 'procurement@regionalhomes.com',
    status: 'pending',
    totalAmount: 892.30,
    items: [
      { name: 'Sur La Table Cookware Set', quantity: 1, price: 179.99 }
    ],
    shippingAddress: {
      street: '789 Pine Road',
      city: 'Los Angeles',
      state: 'CA',
      zipCode: '90001',
      country: 'USA'
    },
    estimatedDelivery: '2024-01-27',
    actualDelivery: null,
    carrier: 'USPS',
    trackingNumber: '94001118992213456789'
  },
  {
    id: 4,
    poNumber: 'PO-2023-156',
    orderDate: '2023-12-10',
    customerName: 'Home Essentials Inc.',
    customerEmail: 'orders@homeessentials.com',
    status: 'completed',
    totalAmount: 3421.80,
    items: [
      { name: 'Gibson Soho Lounge Dinnerware Set', quantity: 8, price: 29.99 },
      { name: 'Sur La Table Cookware Set', quantity: 3, price: 179.99 }
    ],
    shippingAddress: {
      street: '321 Elm Street',
      city: 'Houston',
      state: 'TX',
      zipCode: '77001',
      country: 'USA'
    },
    estimatedDelivery: '2023-12-17',
    actualDelivery: '2023-12-16',
    carrier: 'UPS',
    trackingNumber: '1Z999BB10234567890'
  },
  {
    id: 5,
    poNumber: 'PO-2024-004',
    orderDate: '2024-01-22',
    customerName: 'Metro Kitchen Supply',
    customerEmail: 'purchasing@metrokitch.com',
    status: 'cancelled',
    totalAmount: 567.45,
    items: [
      { name: 'Babish Cast Iron Pan', quantity: 3, price: 34.97 }
    ],
    shippingAddress: {
      street: '654 Maple Drive',
      city: 'Miami',
      state: 'FL',
      zipCode: '33101',
      country: 'USA'
    },
    estimatedDelivery: '2024-01-29',
    actualDelivery: null,
    carrier: 'FedEx',
    trackingNumber: '789012345679'
  },
  {
    id: 6,
    poNumber: 'PO-2024-005',
    orderDate: '2024-01-25',
    customerName: 'National Restaurant Group',
    customerEmail: 'supplies@natrestaurant.com',
    status: 'shipped',
    totalAmount: 4233.25,
    items: [
      { name: 'Sur La Table Cookware Set', quantity: 5, price: 179.99 },
      { name: 'Gibson Soho Lounge Dinnerware Set', quantity: 12, price: 29.99 },
      { name: 'Babish Cast Iron Pan', quantity: 15, price: 34.97 }
    ],
    shippingAddress: {
      street: '987 Cedar Lane',
      city: 'Seattle',
      state: 'WA',
      zipCode: '98101',
      country: 'USA'
    },
    estimatedDelivery: '2024-02-01',
    actualDelivery: null,
    carrier: 'UPS',
    trackingNumber: '1Z999CC10345678901'
  }
];

const statusOptions = ['All', 'pending', 'shipped', 'completed', 'cancelled'];

const OrderSearchPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const ordersPerPage = 6;

  // Get search query from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSearchQuery = searchParams.get('search');
    if (urlSearchQuery) {
      const decodedQuery = decodeURIComponent(urlSearchQuery);
      setSearchTerm(decodedQuery);
      setHasSearched(true);
    }
  }, [location]);

  // Filter and sort orders
  useEffect(() => {
    if (searchTerm || hasSearched || dateFrom || dateTo) {
      let filteredOrders = mockOrders.filter(order => {
        const matchesSearch = order.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            order.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = selectedStatus === 'All' || order.status === selectedStatus;
        
        // Date range filtering
        const orderDate = new Date(order.orderDate);
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo) : null;
        
        const matchesDateFrom = !fromDate || orderDate >= fromDate;
        const matchesDateTo = !toDate || orderDate <= new Date(toDate.setHours(23, 59, 59, 999));
        
        return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
      });

      // Sort by order date (newest first)
      filteredOrders.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate));

      setOrders(filteredOrders);
    } else {
      setOrders([]);
    }
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, dateFrom, dateTo, hasSearched]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim()) {
      setHasSearched(true);
    }
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setHasSearched(true);
  };

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  const handleOrderClick = (order) => {
    navigate(`/order/${order.id}`);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedStatus('All');
    setDateFrom('');
    setDateTo('');
    setHasSearched(false);
    setOrders([]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon color="success" />;
      case 'shipped':
        return <ShippingIcon color="primary" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'cancelled':
        return <CancelledIcon color="error" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'shipped':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"}
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Order Search
        </Typography>
        <Typography 
          variant={isMobile ? "body1" : "h6"}
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Search and filter orders by PO number, customer, or date range
        </Typography>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 4, p: isMobile ? 2 : 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        <Grid container spacing={2}>
          {/* PO Number/Customer Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search PO, customer, or tracking..."
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Date From */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              variant="outlined"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Date To */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              variant="outlined"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              size={isMobile ? "small" : "medium"}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={6} sm={6} md={2}>
            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {statusOptions.map(status => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={6} sm={6} md={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              onClick={clearFilters}
              size={isMobile ? "small" : "medium"}
              sx={{ height: isMobile ? '40px' : '56px' }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Results Count */}
      {orders.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {orders.length} orders found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages}
          </Typography>
        </Box>
      )}

      {/* Orders List */}
      {currentOrders.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {order.poNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(order.orderDate)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {order.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.customerEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        color={getStatusColor(order.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(order.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleOrderClick(order)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, value) => setCurrentPage(value)}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </>
      ) : (
        /* No Results Message */
        <Box sx={{ textAlign: 'center', py: 8 }}>
          {hasSearched || dateFrom || dateTo ? (
            <>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No orders found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your search criteria or date filters
              </Typography>
              <Button variant="contained" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Start searching for orders
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Use the search bar and filters above to find orders
              </Typography>
            </>
          )}
        </Box>
      )}
    </Container>
  );
};

export default OrderSearchPage;